import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';

interface HeroSectionProps {
  onStartTest: () => void;
}

// Real cubes from a thousand rooms. Color, scale, transparency, caption.
const CUBE_PRESETS = [
  { c: '#c43b3b', s: 1, glass: false, t: 'red · solid · one meter wide · № 1' },
  { c: '#d4a938', s: 0.45, glass: false, t: 'gold · ten centimeters · on a stand · № 612' },
  { c: '#2a2d34', s: 1.25, glass: true, t: 'black · see-through · taller than him · № 388' },
  { c: '#c0c3c9', s: 0.7, glass: false, t: 'silver · half a meter · floating · № 149' },
  { c: '#3f6bb4', s: 0.9, glass: true, t: 'blue · glass · low on the floor · № 902' },
];

const SWATCHES = ['#c43b3b', '#3f6bb4', '#d4a938', '#2a2d34', '#c0c3c9', '#4e9464'];

const cubeStyles = `
.tp-viz { background: linear-gradient(180deg,#ffffff 0%,#f4f2ec 72%,#e9e6dd 72.2%,#f2f0e9 100%); }
.tp-scene { perspective: 1100px; }
.tp-cube-scale { transition: transform 1.4s cubic-bezier(.4,0,.2,1); }
.tp-cube { position: relative; width: 140px; height: 140px; transform-style: preserve-3d; animation: tp-turn 26s linear infinite; }
@keyframes tp-turn { from { transform: rotateX(-14deg) rotateY(0); } to { transform: rotateX(-14deg) rotateY(360deg); } }
@media (prefers-reduced-motion: reduce) { .tp-cube { animation: none; transform: rotateX(-14deg) rotateY(32deg); } }
.tp-cube i { position: absolute; inset: 0; backface-visibility: hidden; transition: background 1.2s ease, opacity 1.2s ease; }
.tp-cube.tp-glass i { backface-visibility: visible; opacity: .55; }
.tp-shadow { border-radius: 50%; background: radial-gradient(closest-side, rgba(29,31,36,.22), transparent 72%); transition: transform 1.4s cubic-bezier(.4,0,.2,1); }
`;

function mix(hex: string, withHex: string, amount: number): string {
  const h = (s: string) => [parseInt(s.slice(1, 3), 16), parseInt(s.slice(3, 5), 16), parseInt(s.slice(5, 7), 16)];
  const a = h(hex);
  const b = h(withHex);
  const m = a.map((v, i) => Math.round(v + (b[i] - v) * amount));
  return `rgb(${m[0]},${m[1]},${m[2]})`;
}

export default function HeroSection({ onStartTest }: HeroSectionProps) {
  const [preset, setPreset] = useState(CUBE_PRESETS[0]);
  const [caption, setCaption] = useState(CUBE_PRESETS[0].t);
  const [captionVisible, setCaptionVisible] = useState(true);
  const [cycling, setCycling] = useState(true);
  const idxRef = useRef(0);

  useEffect(() => {
    if (!cycling) return;
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setInterval(() => {
      idxRef.current = (idxRef.current + 1) % CUBE_PRESETS.length;
      const next = CUBE_PRESETS[idxRef.current];
      setPreset(next);
      setCaptionVisible(false);
      setTimeout(() => {
        setCaption(next.t);
        setCaptionVisible(true);
      }, 450);
    }, 4200);
    return () => clearInterval(timer);
  }, [cycling]);

  const pickColor = (color: string) => {
    setCycling(false);
    setPreset({ c: color, s: 1, glass: false, t: '' });
    setCaption('yours, maybe · take the test to find out');
    setCaptionVisible(true);
  };

  const faces = [
    { bg: preset.c, tf: 'translateZ(70px)' },
    { bg: preset.c, tf: 'rotateY(180deg) translateZ(70px)' },
    { bg: mix(preset.c, '#1d1f24', 0.28), tf: 'rotateY(90deg) translateZ(70px)' },
    { bg: mix(preset.c, '#1d1f24', 0.28), tf: 'rotateY(-90deg) translateZ(70px)' },
    { bg: mix(preset.c, '#ffffff', 0.32), tf: 'rotateX(90deg) translateZ(70px)' },
    { bg: mix(preset.c, '#1d1f24', 0.4), tf: 'rotateX(-90deg) translateZ(70px)' },
  ];

  return (
    <section className="px-4 sm:px-6 lg:px-8">
      <style>{cubeStyles}</style>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center py-10 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#c43b3b] mb-4">
            A five minute voice conversation
          </p>
          <h1 className="font-serif font-medium text-[#1d1f24] text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
            Close your eyes.
            <br />
            Imagine a completely <em className="italic">white room.</em>
          </h1>
          <p className="mt-5 text-lg text-[#5d606a] max-w-xl">
            In that room a cube will appear. Then a ladder, a horse, flowers, and a storm. What you see is not random.
            Psychiatrist <b className="text-[#1d1f24] font-semibold">Bram van der Boom</b> has asked more than a thousand
            people to describe that room, face to face, since 2014. Now his voice asks you.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={onStartTest}
              className="inline-flex items-center gap-3 bg-[#c43b3b] text-white font-bold py-3.5 px-8 rounded-full text-lg shadow-[0_6px_18px_rgba(196,59,59,0.3)] hover:bg-[#b03434] transition-colors duration-200"
            >
              <Play className="w-5 h-5" />
              Take the Test, Free
            </motion.button>
            <span className="text-sm text-[#5d606a]">5 minutes &middot; just your voice &middot; nothing to type</span>
          </div>
          <div className="mt-9 flex flex-wrap gap-7">
            <div className="border-l-2 border-[#c43b3b] pl-3.5 max-w-[220px]">
              <div className="font-serif text-2xl text-[#1d1f24]">1,000+</div>
              <div className="text-xs text-[#5d606a] leading-snug">readings given in person since 2014</div>
            </div>
            <div className="border-l-2 border-[#c43b3b] pl-3.5 max-w-[220px]">
              <div className="font-serif text-2xl text-[#1d1f24]">1</div>
              <div className="text-xs text-[#5d606a] leading-snug">practicing psychiatrist behind every word</div>
            </div>
            <div className="border-l-2 border-[#c43b3b] pl-3.5 max-w-[220px]">
              <div className="font-serif text-2xl text-[#1d1f24]">0</div>
              <div className="text-xs text-[#5d606a] leading-snug">fake reviews on this page</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="rounded-2xl border border-[#e4e1d8] overflow-hidden bg-white"
        >
          <div className="tp-viz flex flex-col items-center justify-center min-h-[300px] py-7">
            <div className="tp-scene h-[210px] flex items-center justify-center">
              <div className="tp-cube-scale" style={{ transform: `scale(${preset.s})` }}>
                <div className={`tp-cube${preset.glass ? ' tp-glass' : ''}`}>
                  {faces.map((f, i) => (
                    <i key={i} style={{ background: f.bg, transform: f.tf }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="tp-shadow w-[190px] h-[34px] -mt-1.5" style={{ transform: `scaleX(${preset.s})` }} />
            <div
              className="font-serif italic text-[#5d606a] mt-4 text-center min-h-[1.6em] px-4 transition-opacity duration-500"
              style={{ opacity: captionVisible ? 1 : 0 }}
            >
              {caption}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2.5 py-3.5 px-4 bg-white border-t border-[#e4e1d8]">
            <span className="font-serif italic text-[#5d606a] mr-1">What color is yours?</span>
            {SWATCHES.map((color) => (
              <button
                key={color}
                aria-label={`cube in ${color}`}
                onClick={() => pickColor(color)}
                className="w-6 h-6 rounded-full border-2 border-white shadow-[0_1px_4px_rgba(0,0,0,0.25)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1d1f24]"
                style={{ background: color }}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
